import { useState } from "react";

// Teclado virtual da simulação de celular/tablet do UAT (UatViewportShortcut).
// Cores fixas de propósito: faz parte da "moldura" do aparelho, que é sempre
// escura, igual nos dois temas.
const COLORS = {
  panel: "#221f2c",
  key: "#3a3647",
  special: "#2f2b3a",
  text: "#f4f2f8",
  muted: "#9892ab",
  accent: "#FF6A21",
};

const TEXT_ROWS = [
  "1234567890".split(""),
  "qwertyuiop".split(""),
  "asdfghjkl".split(""),
];
const LETTERS_LAST_ROW = "zxcvbnm".split("");

export const KEYBOARD_TRANSITION_MS = 220;

function Key({ label, value, flex = 1, special = false, active = false, onPress }) {
  return (
    <button
      type="button"
      // Não deixa o clique roubar o foco do campo que está sendo digitado.
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onPress(value ?? label)}
      style={{
        flex,
        height: 40,
        minWidth: 0,
        borderRadius: 6,
        border: 0,
        background: active ? COLORS.text : special ? COLORS.special : COLORS.key,
        color: active ? COLORS.panel : COLORS.text,
        fontSize: 17,
        cursor: "pointer",
        padding: 0,
      }}
    >
      {label}
    </button>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 5 }}>{children}</div>;
}

export default function UatVirtualKeyboard({ open, layout, height, onKey }) {
  const [shift, setShift] = useState(false);

  const press = (value) => {
    onKey(value);
    if (value.length === 1 && value !== " ") setShift(false);
  };
  const letter = (char) => (shift ? char.toUpperCase() : char);

  return (
    <div
      role="group"
      aria-label="Teclado virtual da simulação"
      aria-hidden={!open}
      style={{
        height: open ? height : 0,
        overflow: "hidden",
        flex: "0 0 auto",
        background: COLORS.panel,
        transition: `height ${KEYBOARD_TRANSITION_MS}ms ease-out`,
      }}
    >
      <div style={{ height, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "4px 10px",
            borderBottom: `1px solid ${COLORS.special}`,
          }}
        >
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onKey("Done")}
            style={{
              border: 0,
              background: "transparent",
              color: COLORS.accent,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px 6px",
            }}
          >
            Concluído
          </button>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 6,
            padding: "6px 5px",
          }}
        >
          {layout === "numeric" ? (
            <>
              <Row>
                {["1", "2", "3"].map((k) => (
                  <Key key={k} label={k} onPress={press} />
                ))}
                <Key label="⌫" value="Backspace" special onPress={press} />
              </Row>
              <Row>
                {["4", "5", "6"].map((k) => (
                  <Key key={k} label={k} onPress={press} />
                ))}
                <Key label="↵" value="Enter" special onPress={press} />
              </Row>
              <Row>
                {["7", "8", "9"].map((k) => (
                  <Key key={k} label={k} onPress={press} />
                ))}
                <Key label="." special onPress={press} />
              </Row>
              <Row>
                <Key label="," special onPress={press} />
                <Key label="0" flex={2} onPress={press} />
                <Key label="-" special onPress={press} />
              </Row>
            </>
          ) : (
            <>
              {TEXT_ROWS.map((row, index) => (
                <Row key={index}>
                  {row.map((k) => (
                    <Key
                      key={k}
                      label={index === 0 ? k : letter(k)}
                      onPress={press}
                    />
                  ))}
                </Row>
              ))}
              <Row>
                <Key
                  label="⇧"
                  flex={1.5}
                  special
                  active={shift}
                  onPress={() => setShift((current) => !current)}
                />
                {LETTERS_LAST_ROW.map((k) => (
                  <Key key={k} label={letter(k)} onPress={press} />
                ))}
                <Key label="⌫" value="Backspace" flex={1.5} special onPress={press} />
              </Row>
              <Row>
                <Key label="." special onPress={press} />
                <Key label="," special onPress={press} />
                <Key label="espaço" value=" " flex={4} onPress={press} />
                <Key label="@" special onPress={press} />
                <Key label="↵" value="Enter" flex={1.5} special onPress={press} />
              </Row>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
